import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import Popup from 'components/popups/Popup'
import { displayPrice } from 'utils/string'
import React, { useState } from 'react'
import Range from 'components/Range'

interface InsurancePopupProps {
  data: AvailableInsurance
  previousValues?: InsuranceState
  updateValues: (values?: InsuranceState) => void
}

export interface InsuranceState {
  v: string
  variantId: string
  variantName: string
  grossPrice: string
}

interface AvailableInsurance {
  variants: AvailableInsuranceVariant[]
}

interface AvailableInsuranceVariant {
  id: string
  name: string
  selected: boolean
  description: string
  pricelist: AvailableInsurancePricelist[]
  /** publiczny adres regulaminu (backend sam wybiera język i formę); null = brak regulaminu */
  regulationsUrl: string | null
  regulationsType: 'html' | 'pdf' | null
  /** język, w którym regulamin faktycznie jest - może się różnić od języka aplikacji */
  regulationsLocale: string | null
}

interface AvailableInsurancePricelist {
  gross_price: string
  v: string
  r: number
}

const InsurancePopup: React.FC<InsurancePopupProps> = ({ previousValues, data, updateValues }) => {
  const selectedVariant = data?.variants?.find(v => v.selected) || data?.variants?.[0]
  const [warning, setWarning] = useState(false)
  const [terms, setTerms] = useState(!!previousValues)
  const [showRegulations, setShowRegulations] = useState(false)
  const [values, setValues] = useState<InsuranceState>(previousValues || {
    variantId: selectedVariant?.id,
    variantName: selectedVariant?.name,
    v: selectedVariant.pricelist?.[1] ? selectedVariant.pricelist[1]?.v : selectedVariant.pricelist?.[0]?.v,
    grossPrice: selectedVariant.pricelist?.[1] ? selectedVariant.pricelist[1]?.gross_price : selectedVariant.pricelist?.[0]?.gross_price
  })

  const { t, lang } = useTranslation('basket')
  const { goBack } = useHistory()

  // cennik i regulamin z wariantu wybranego w popupie, nie z domyślnego
  const currentVariant = data?.variants?.find(v => v.id === values.variantId) || selectedVariant
  const { regulationsUrl, regulationsType, regulationsLocale } = currentVariant || {}

  const openRegulations = (e: React.MouseEvent) => {
    // link siedzi w etykiecie checkboxa - bez tego klik przełączyłby zgodę
    e.preventDefault()
    e.stopPropagation()
    if (!regulationsUrl) return
    if (regulationsType === 'pdf') {
      window.open(regulationsUrl, '_blank', 'noopener')
    } else {
      setShowRegulations(true)
    }
  }

  if (showRegulations && regulationsUrl) {
    // podmieniamy zawartość zamiast otwierać drugi popup - stan wyboru zostaje w tym komponencie
    return <ConsentPreviewPopup
      type='terms'
      title={t('insuranceTermsTitle')}
      endpoint={regulationsUrl}
      close={() => setShowRegulations(false)}
    />
  }

  const termsLabel = t('acceptedInsuranceTerms-array')
  const localeNote = regulationsUrl && regulationsLocale && regulationsLocale !== lang
    ? ` (${t('insuranceTermsLocale')} ${regulationsLocale.toUpperCase()})`
    : ''

  const priceListData = currentVariant?.pricelist.reduce((r, priceListItem) => {
    r.options.push(priceListItem.v)
    r.optionsPosition.push(priceListItem.r)
    return r
  }, {
    options: [] as string[],
    optionsPosition: [] as number[]
  })

  return (
    <Popup darkBg>
      <div className='insurance__popup'>
        {data.variants.length > 1 && <>
          <p>{t('insuranceOptions')}</p>
          <div className='insurance__options'>
            {data.variants.map(({ id, name, description, pricelist }) => {
              const isSelected = values.variantId === id
              return <div
                key={id}
                className={`insurance__option${isSelected ? ' --selected' : ''}`}
                onClick={() => {
                  if (isSelected) return
                  const priceListItem = pricelist?.[1] || pricelist?.[0]
                  setValues({
                    variantId: id,
                    variantName: name,
                    v: priceListItem?.v,
                    grossPrice: priceListItem?.gross_price
                  })
                }}>
                <div className='insurance__option__title'>{name}</div>
                <div className='insurance__option__desc'>{description}</div>
                <div className='insurance__option__circle'>
                  <i className='icon icon--check' />
                </div>
              </div>
            })}
          </div>
        </>}

        <p>{t('insuranceSum')}: {displayPrice(values.v)}</p>
        <Range
          value={values.v}
          options={priceListData.options as never[]} // 🙈
          optionsPosition={priceListData.optionsPosition as never[]} // 🙈
          updateValue={(v: string) => {
            const grossPrice = currentVariant.pricelist.find(e => e.v === v)?.gross_price || ''
            setValues(prev => ({ ...prev, v, grossPrice }))
          }}
        />

        <Checkbox
          required
          name='terms'
          warning={warning}
          checked={terms}
          label={<span>
            {termsLabel[0]}{' '}
            {regulationsUrl
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              ? <a className='--link' href={regulationsUrl} onClick={openRegulations}>{termsLabel[1]}</a>
              : termsLabel[1]}
            {localeNote}
          </span>}
          changeHandler={() => {
            if (warning) {
              setWarning(false)
            }
            setTerms(t => !t)
          }}
        />

        <div className='insurance__buttons'>
          <ArrowBtn
            enlarged
            showArrow={false}
            text={`${t('add')} (${t('monthly')} ${displayPrice(values.grossPrice)})`}
            action={() => {
              if (terms) {
                updateValues(values)
                goBack()
              } else {
                setWarning(true)
              }
            }}
          />
          <BackBtn
            text={t(previousValues ? 'delete' : 'cancel')}
            action={() => {
              if (previousValues) {
                updateValues()
              }
              goBack()
            }}
          />
        </div>
      </div>
    </Popup>
  )
}

export default InsurancePopup